import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient";

export const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getBusinessProfile = async (
    authId,
    email,
    accessToken
  ) => {
    if (!authId) {
      console.warn(
        "[Auth] getBusinessProfile called with null authId"
      );
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_FUNCTION_URL}/getBusinessProfile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            auth_id: authId,
            business_email: email,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Edge Function error: ${response.status}`
        );
      }

      const data = await response.json();

      console.log(
        "[Auth] Edge Function raw data:",
        data
      );

      const mergedProfile = {
        ...(data?.business || {}),
        ...(data?.profile || {}),
        ...(data?.settings || {}),
        ...(data?.tool_order || {}),

        // Preserve business primary key
        business_id:
          data?.business?.id ?? null,

        // Preserve business UUID
        business_uuid:
          data?.business?.business_uuid ??
          null,
      };

      setProfile(mergedProfile);
      setError(null);
    } catch (err) {
      console.error(
        "[Auth] ❌ Exception in getBusinessProfile:",
        err.message
      );

      setProfile({
        id: authId,
      });

      setError(err);
    } finally {
      setLoading(false);
      console.log(
        "[Auth] ✅ getBusinessProfile complete"
      );
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initSession = async () => {
      setLoading(true);

      try {
        const { data } =
          await supabase.auth.getSession();

        const currentUser =
          data?.session?.user || null;

        const accessToken =
          data?.session?.access_token;

        if (!isMounted) return;

        if (currentUser?.id) {
          setUser(currentUser);

          await getBusinessProfile(
            currentUser.id,
            currentUser.email,
            accessToken
          );
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch (err) {
        setError(err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    const { data: listener } =
      supabase.auth.onAuthStateChange(
        async (_event, session) => {
          const currentUser =
            session?.user || null;

          const accessToken =
            session?.access_token;

          setLoading(true);

          if (currentUser?.id) {
            setUser(currentUser);

            await getBusinessProfile(
              currentUser.id,
              currentUser.email,
              accessToken
            );
          } else {
            setUser(null);
            setProfile(null);
          }

          setLoading(false);
        }
      );

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const signIn = async ({
    email,
    password,
  }) => {
    try {
      const { data, error } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (error) {
        setError(error);
        return {
          message: error.message,
        };
      }

      const currentUser =
        data?.user || null;

      const accessToken =
        data?.session?.access_token;

      setUser(currentUser);

      if (currentUser?.id) {
        await getBusinessProfile(
          currentUser.id,
          currentUser.email,
          accessToken
        );
      }

      return null;
    } catch (err) {
      setError(err);

      return {
        message: err.message,
      };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();

    setUser(null);
    setProfile(null);
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        error,
        signIn,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}